import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'
import {migrate} from "@/migrate.ts";

function serializeError(error: unknown): unknown {
  const seen = new WeakSet<object>()

  const walk = (value: unknown): unknown => {
    if (value instanceof Error) {
      const serialized: Record<string, unknown> = {
        name: value.name,
        message: value.message,
        stack: value.stack,
      }

      if ('cause' in value && (value as any).cause !== undefined) {
        serialized.cause = walk((value as any).cause)
      }

      for (const key of Object.getOwnPropertyNames(value)) {
        if (key in serialized) continue
        serialized[key] = walk((value as any)[key])
      }

      return serialized
    }

    if (value && typeof value === 'object') {
      if (seen.has(value as object)) return '[Circular]'
      seen.add(value as object)

      if (Array.isArray(value)) {
        return value.map((item) => walk(item))
      }

      const output: Record<string, unknown> = {}
      for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
        output[key] = walk(inner)
      }
      return output
    }

    return value
  }

  return walk(error)
}

function logDetailedError(label: string, error: unknown): void {
  console.error(label, error)
  try {
    const serialized = serializeError(error)
    console.error(`${label} (serialized):`, serialized)
  } catch (serializationError) {
    console.error(`${label} (serialization failed):`, serializationError)
  }
}

window.addEventListener('unhandledrejection', (event) => {
  logDetailedError('Unhandled Promise Rejection', event.reason)
})

window.addEventListener('error', (event) => {
  logDetailedError('Unhandled Error Event', event.error ?? event.message)
})

migrate()
  .then(() => {
    console.log('Migrated! Starting app...');

    const app = createApp(App)

    app.use(createPinia())
    app.use(router)

    app.mount('#app')
  })
  .catch((error) => {
    logDetailedError('App migration/startup failed', error)
  })
